import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, fromEvent } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter
} from 'rxjs/operators';
import { ToastService } from 'src/app/shared/services/angular-service/toast.service';
import { CompanyService } from 'src/app/shared/services/node-api/company.service';

@Component({
  selector: 'app-company-details-component',
  templateUrl: './company-details-component.component.html',
  styleUrls: ['./company-details-component.component.scss'],
})
export class CompanyDetailsComponentComponent implements OnInit, AfterViewInit {
  @ViewChildren(
    'comp_overview, comp_funding, comp_financials, comp_people, comp_roc,comp_timeline'
  )
  observedElements!: QueryList<ElementRef>;

  private scrollSubscription!: Subscription;

  @Input() company_name: any;
  @Input() tab: any;
  // lastTab: any;
  @Input() cin: any;
  @Input() company_url: String;
  @Input() id: String;
  overview = true;
  funding = true;
  financials = true;
  traction = true;
  people = true;
  news = true;
  display = false;
  roc: boolean = true;
  public all_data;
  isAutoScroll = true;
  timeline: boolean = true;

  constructor(
    private router: Router,
    private companyService: CompanyService,
    private toastService: ToastService
  ) {
      this.companyService.autoScroll$.subscribe((val) => {
        this.isAutoScroll = val;
      })
  }

  private scrollToElement(selector: string) {
    const element = document.querySelector(selector);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  private lastScrollTop = 0;
  private scrollThreshold = 50; // Define what you consider a significant change

  private hasSignificantScroll(): boolean {
    const currentScrollTop =
      window.pageYOffset || document.documentElement.scrollTop;
    if (
      Math.abs(currentScrollTop - this.lastScrollTop) > this.scrollThreshold
    ) {
      this.lastScrollTop = currentScrollTop;
      return true;
    }
    return false;
  }

  private setupScrollListener(): void {
    this.scrollSubscription = fromEvent(window, 'scroll')
      .pipe(
        debounceTime(50),
        filter(() => this.hasSignificantScroll()), // Filter events for significant scrolls
        distinctUntilChanged() // Ensure duplicates are not emitted
      )
      .subscribe(() => this.checkElementsInView());
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.setupScrollListener();
    }, 500);
  }

  ngOnDestroy(): void {
    if (this.scrollSubscription) {
      this.scrollSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.company_url = this.company_url;
    this.scrollToTab(this.tab);
  }

  ngOnInit(): void {
    this.getHistoricalData();
  }

  getHistoricalData() {
    let request = { cin: this.cin };
    this.companyService
      .getCompanyHistoricalDetails(request)
      .then((data: any) => {
        if (data.message) {
          this.toastService.addSingle('error', 'Error', data.message);
          this.router.navigate(['/company']);
        } else {
          this.all_data = data;
          let initialValue = {};

          this.all_data['roc_product_list'] = this.all_data.product_list_arr.filter(
            (item) => item.type === 'ROC'
          );
          this.all_data['pls_product_list'] = this.all_data.product_list_arr.filter(
            (item) => item.type === 'PLS'
          );
          this.all_data['bss_product_list'] = this.all_data.product_list_arr.filter(
            (item) => item.type === 'BSS'
          );
          this.all_data.product_list = this.all_data.product_list_arr.filter(
            (item) =>
              item.type !== 'BSS' && item.type !== 'ROC' && item.type !== 'PLS'
          );

          this.all_data.product_list = this.all_data.product_list.reduce(
            (obj, item) => {
              return {
                ...obj,
                [item['type']]: item,
              };
            },
            initialValue
          );
        }
      })
      .catch((err: any) => {
        console.log(err);
      });
  }

  isVisible = false;

  private checkElementsInView(): void {
    // console.log('checkElementsInView');
    let lastVisibleElementRef = null; // To store the last visible element

    this.observedElements.forEach((elementRef) => {
      const rect = elementRef.nativeElement.getBoundingClientRect();
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;
      const isVisible = rect.top <= viewportHeight && rect.bottom >= 0;

      // Update lastVisibleElementRef each time an element is visible
      if (isVisible) {
        lastVisibleElementRef = elementRef;
      }
    });

    // Act only on the last visible element found, if any
    if (lastVisibleElementRef) {
      // console.log('isVisible', lastVisibleElementRef.nativeElement.id);
      const tabId = lastVisibleElementRef.nativeElement.id.split('_')[1];
      // console.log('tabId', tabId);
      this.navigateToTab(tabId);
    }
  }

  scrollToTab(tabId) {
    // console.log('scrollToTab', tabId);
    if (this.all_data === undefined || this.isAutoScroll) {
      if (tabId.includes('overview')) {
        this.scrollToElement('.company-details');
      } else if (tabId.includes('funding')) {
        this.scrollToElement('.company-funding');
      } else if (tabId.includes('financials')) {
        this.scrollToElement('.company-financial');
      } else if (tabId.includes('traction')) {
        this.scrollToElement('.company-details');
      } else if (tabId.includes('people')) {
        this.scrollToElement('.company-people');
      } else if (tabId.includes('news')) {
        this.scrollToElement('.company-news');
      } else if (tabId.includes('roc')) {
        this.scrollToElement('.company-roc');
      }
      this.companyService.updateAutoScroll(false);
    }
  }

  navigateToTab(tabId) {
    if (tabId) {
      this.companyService.updateAutoScroll(false);
      this.tab = tabId;
      if (tabId.includes('overview')) {
        this.router.navigate(['/company/' + this.company_url + '/' + tabId], { replaceUrl: true });
        this.overview = true;
      } else if (tabId.includes('funding')) {
        this.router.navigate(['/company/' + this.company_url + '/' + tabId], { replaceUrl: true });
        this.funding = true;
      } else if (tabId.includes('financials')) {
        this.router.navigate(['/company/' + this.company_url + '/' + tabId], { replaceUrl: true });
        this.financials = true;
      } else if (tabId.includes('traction')) {
        this.router.navigate(['/company/' + this.company_url + '/' + tabId], { replaceUrl: true });
        this.traction = true;
      } else if (tabId.includes('people')) {
        this.router.navigate(['/company/' + this.company_url + '/' + tabId], { replaceUrl: true });
        this.people = true;
      } else if (tabId.includes('news')) {
        this.router.navigate(['/company/' + this.company_url + '/' + tabId], { replaceUrl: true });
        this.news = true;
      } else if (tabId.includes('roc')) {
        this.router.navigate(['/company/' + this.company_url + '/' + tabId], { replaceUrl: true });
        this.roc = true;
      }
      else if (tabId.includes('timeline')) {
        this.router.navigate(['/company/' + this.company_url + '/' + tabId], { replaceUrl: true });
        this.timeline = true;
      }
    }
  }
}
